import { business, getGuide, getService, guides, services } from './site';

const defaultImage = '/images/owner-projects/owner-project-social.webp';
const caseByCaseServiceSlugs = new Set(['chimney-masonry-repair']);

const fixedMeta = {
  '/': {
    title: "Premium Hardscapes & Masonry in Havelock, NC | Vic's Bricks",
    description:
      'Premium paver patios, driveways, and custom masonry in Havelock and Eastern NC. Explore real project work and request an estimate from Vic’s Bricks.',
  },
  '/services': {
    title: "Hardscape and Masonry Services in Eastern NC | Vic's Bricks",
    description:
      'Explore new installations and repairs for pavers, brick, block, tile, concrete, retaining walls, faux stone, drainage, and grading.',
  },
  '/projects': {
    title: "Hardscape and Masonry Project Gallery | Vic's Bricks",
    description:
      'Explore real paver patios, driveways, masonry, tile, and manufactured stone projects, with the details behind a premium finish in Eastern North Carolina.',
  },
  '/service-area': {
    title: "Service Area | Havelock and Eastern NC | Vic's Bricks",
    description:
      'Vic’s Bricks serves hardscape, brick, block, tile, concrete, faux stone, drainage, and grading projects across Eastern North Carolina.',
  },
  '/about': {
    title: "About Vic's Bricks | Eastern NC Masonry Contractor",
    description:
      'Meet Vic’s Bricks, an NCCER-trained and insured hardscape and masonry contractor serving Havelock and Eastern North Carolina.',
  },
  '/guides': {
    title: "Hardscape and Masonry Project Guides | Vic's Bricks",
    description:
      'Plain-language guidance for planning pavers, brick repair, concrete, retaining walls, drainage, and other hardscape projects.',
  },
  '/contact': {
    title: "Request a Hardscape or Masonry Estimate | Vic's Bricks",
    description:
      'Tell Vic’s Bricks about a paver, brick, block, tile, concrete, faux stone, drainage, or grading project in Eastern North Carolina.',
  },
  '/privacy': {
    title: "Privacy | Vic's Bricks",
    description: 'How Vic’s Bricks uses information submitted through the project inquiry form.',
    noindex: false,
  },
};

export const staticRoutes = [
  '/',
  '/services',
  ...services
    .filter((service) => !caseByCaseServiceSlugs.has(service.slug))
    .map((service) => `/${service.slug}`),
  '/projects',
  '/service-area',
  '/about',
  '/guides',
  ...guides.map((guide) => `/guides/${guide.slug}`),
  '/contact',
  '/privacy',
];

export const prerenderRoutes = [
  ...staticRoutes,
  ...services
    .filter((service) => caseByCaseServiceSlugs.has(service.slug))
    .map((service) => `/${service.slug}`),
];

function pathParts(pathname) {
  return pathname.split('/').filter(Boolean);
}

export function getMetaForPath(inputPath) {
  const pathname = inputPath.split('?')[0].replace(/\/$/, '') || '/';
  const parts = pathParts(pathname);
  const service = parts.length === 1 ? getService(parts[0]) : null;
  const guide = parts.length === 2 && parts[0] === 'guides' ? getGuide(parts[1]) : null;
  const fixed = fixedMeta[pathname];

  let meta = fixed;
  if (service) {
    meta = {
      title: service.seoTitle || `${service.name} in Eastern NC | Vic's Bricks`,
      description: service.summary,
      image: service.image,
      type: 'service',
      service,
      noindex: caseByCaseServiceSlugs.has(service.slug),
    };
  }
  if (guide) {
    meta = {
      title: `${guide.title} | Vic's Bricks`,
      description: guide.description,
      image: guide.image,
      type: 'article',
      guide,
    };
  }
  if (!meta) {
    meta = {
      title: "Page Not Found | Vic's Bricks",
      description: 'The page you requested could not be found.',
      noindex: true,
    };
  }

  const canonical = `${business.siteUrl}${pathname === '/' ? '/' : `${pathname}/`}`;
  const image = new URL(meta.image || defaultImage, business.siteUrl).href;
  const ogType = meta.type === 'article' ? 'article' : 'website';
  return { ...meta, pathname, canonical, image, ogType };
}

export function getStructuredData(meta) {
  const organization = {
    '@type': 'Organization',
    '@id': `${business.siteUrl}/#organization`,
    name: business.name,
    url: `${business.siteUrl}/`,
    logo: `${business.siteUrl}/icon-512.png`,
    telephone: business.phoneDisplay,
    email: business.email,
    description: 'Hardscape, paver, brick, block, tile, concrete, faux stone, drainage, and grading services across Eastern North Carolina.',
    areaServed: {
      '@type': 'AdministrativeArea',
      name: business.region,
    },
  };

  const graph = [organization];
  if (meta.type === 'service' && meta.service) {
    graph.push({
      '@type': 'Service',
      '@id': `${meta.canonical}#service`,
      name: meta.service.name,
      description: meta.service.summary,
      url: meta.canonical,
      provider: { '@id': organization['@id'] },
      areaServed: organization.areaServed,
      serviceType: meta.service.name,
    });
  }
  if (meta.type === 'article' && meta.guide) {
    graph.push({
      '@type': 'Article',
      '@id': `${meta.canonical}#article`,
      headline: meta.guide.title,
      description: meta.guide.description,
      image: meta.image,
      mainEntityOfPage: meta.canonical,
      author: { '@id': organization['@id'] },
      publisher: { '@id': organization['@id'] },
    });
  }

  if (meta.pathname !== '/') {
    const currentName = meta.service?.name || meta.guide?.title || meta.title.split('|')[0].trim();
    const itemListElement = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${business.siteUrl}/` },
    ];
    if (meta.service) {
      itemListElement.push({ '@type': 'ListItem', position: 2, name: 'Services', item: `${business.siteUrl}/services/` });
    } else if (meta.guide) {
      itemListElement.push({ '@type': 'ListItem', position: 2, name: 'Guides', item: `${business.siteUrl}/guides/` });
    }
    itemListElement.push({ '@type': 'ListItem', position: itemListElement.length + 1, name: currentName, item: meta.canonical });
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${meta.canonical}#breadcrumb`,
      itemListElement,
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}
